import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { onErrorAlert } from 'common/helper';
import { BehaviorSubject, Observable, Subject, Subscription, catchError, finalize, map, of, tap } from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { FormStore } from '../common/form.store';
import { EEvent, TPostMessage } from '../core/Events';
import { ParentWindowService } from '../core/parent-window.service';
import { QuestionnaireContactDetail, QuestionnaireInfo, IQuestionnaireSaveRequestHttp, IQuestionnaireItemSaveRequestHttp } from 'questionnaire/model/questionnaire.model';
import { DetailGroupInfo, DetailInfo } from 'common/model';
import { FormBuilder } from '@angular/forms';
import { assignDetailInfoValue, compareValidationResult, prepareDetailDataRequest } from 'shared/shared.helper';
import { IDetailInfoDataHttp } from 'common/model/common.model';
import { QuestionnaireService } from '../services/questionnaire.service';

@Component({
    templateUrl: './questionnaire-form.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class QuestionnaireFormComponent implements OnInit {
  @ViewChild('container') container: ElementRef;

  @Input() set baseApiUrl(apiUrl: string | undefined) {
    if (apiUrl) {
      environment.baseUrl = apiUrl;
    }
  }

  @Input() set baseFormController(controller: string | undefined) {
    if (controller) {
      environment.clientFormController = controller;
    }
  }

  @Input() token: string | undefined;
  @Input() preview = false;

  public questionnaireGroupInfosListObs!: Observable<DetailGroupInfo[][]>;
  public questionnaires: QuestionnaireInfo[] = [];
  public titles: string[] = [];
  public currentFormIndex = 0;

  public detailsInfos: DetailInfo[] = [];
  public questionnaireContactDetail: QuestionnaireContactDetail;

  public captchaSiteKeyObs!: Observable<string>;
  public recaptcha: string;

  private clientToken?: string | null = null;
  private formGuid?: string | null = null;
  public isValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public pageData: any = {
    formGroup: this.fb.group({}),
    gridRowLists: [],
    formGuid: '',
    activitySessionGuid: '',
    isUnsaved: false,
    formSaveSuccess$: new Subject<boolean>()
  };

  public processing$ = new BehaviorSubject<boolean>(false);
  public submitted = false;

  private subs = new Subscription();

  constructor(
    route: ActivatedRoute,
    private fb: FormBuilder,
    private router: Router,
    private alert: ToastrService,
    private parentWindow: ParentWindowService,
    private questionnaireService: QuestionnaireService,
  ) {
    this.clientToken = route.snapshot.queryParamMap.get('formurl'); // For client
    this.formGuid = route.snapshot.paramMap.get('guid'); // For preview

    const sub = FormStore.resize$.subscribe(this.onResize.bind(this));
    this.subs.add(sub);
  }

  @HostListener('window:beforeunload', ['$event'])
  checkReload(event: any) {
    event.preventDefault();
    if (this.pageData.isUnsaved) {
      event.returnValue =
        'You have unsaved changes! Are you sure you want to leave?';
    }
  }

  ngOnInit() {
    this.init();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private init() {
    if (this.clientToken) {
      FormStore.token = this.clientToken;
      this.getFormDataFromUrl();
      this.getRecaptchaSiteKey();
    } else if (this.formGuid && this.token) {
      FormStore.token = this.token;
      this.getFormDataFromGuid(this.formGuid);
    } else {
      this.isValid$.next(false);
    }
  }

  private getFormDataFromUrl() {
    this.questionnaireGroupInfosListObs = this.questionnaireService.getQuestionnaireInfo().pipe(
      tap(res => this.prepareClientInputContents(res)),
      map(items => {
        this.questionnaires = items;
        this.titles = items.map(item => item.scheduleTitle);
        return items.map(item => item.groupInfos)
      }),
      catchError(this.onGetError.bind(this)),
      finalize(() => {
        FormStore.resize$.next();
      })
    );

  }

  private getFormDataFromGuid(guid: string) {
    this.questionnaireGroupInfosListObs = this.questionnaireService.getQuestionnaireInfo().pipe( // guid
      tap(res => this.prepareClientInputContents(res)),
      map(items => {
        this.questionnaires = items;
        this.titles = items.map(item => item.scheduleTitle);
        return items.map(item => item.groupInfos)
      }),
      catchError(this.onGetError.bind(this)),
    );
  }

  private prepareClientInputContents(infoList: QuestionnaireInfo[]) {
    this.detailsInfos = [];
    infoList.forEach((info) => {
      info.groupInfos.forEach(group => group.detailInfos.forEach(inputContent => {
        group.detailInfos.forEach(detailInfo => {
          assignDetailInfoValue(detailInfo, info.programDetailValue);
        });
        this.detailsInfos.push(inputContent);
      }))
    })
  }

  private getRecaptchaSiteKey() {
    this.captchaSiteKeyObs = this.questionnaireService.getCaptchaInfo().pipe(
      map(res => res.dataSiteKey),
      catchError(this.onGetError.bind(this))
    );
  }

  public recaptchaResolved(val: string) {
    this.recaptcha = val;
  }

  private onGetError() {
    this.isValid$.next(false);
    return of();
  }


  public save(formObj: any) {
    this.submitted = true;

    const body = this.prepareBody(formObj);
    if (!body || !this.clientToken) { return; }
    this.processing$.next(true);

    this.questionnaireService.saveQuestionnaireAnswer(body).pipe(
      finalize(() => this.processing$.next(false))
    ).subscribe({
      next: (res) => {
        FormStore.reset$.next();
        this.pageData.isUnsaved = false;
        this.pageData.formSaveSuccess$.next(true);
        const state = { successMessage: res.message };
        this.router.navigateByUrl('/success', { state });
      },
      error: (err) => onErrorAlert(this.alert, err)
    });
  }

  private prepareBody(formObjs: any[]): IQuestionnaireSaveRequestHttp | null {

    if (!this.clientToken) { return null; }

    if (!this.recaptcha) {
      this.alert.error('Please validate captcha!');
      return
    }

    const itemAnswers: IQuestionnaireItemSaveRequestHttp[] = [];
    let validationFail = false;
    formObjs.forEach((formObj, index) => {
      const detailData: IDetailInfoDataHttp[] = prepareDetailDataRequest(formObj, this.detailsInfos);

      const questionnaire = this.questionnaires[index];
      itemAnswers.push({
        detailData,
        projectId: questionnaire.projectId,
        contactGuid: questionnaire.contactGuid,
        evideInputContentFormGuid: questionnaire.evideInputContentFormGuid,
        questionnaireItemGuid: questionnaire.questionnaireItemGuid
      })
    });

    // Conditional Form Groups: round-trip each item's selectedVariantGuid back to the server.
    // Public webform does not currently expose a variant selector — variant is whatever the
    // server delivered (typically null for client-driven fills); preserves the value for read-only views.
    itemAnswers.forEach((itemAnswer, index) => {
      itemAnswer.selectedVariantGuid = this.questionnaires[index]?.selectedVariantGuid ?? null;
    });

    itemAnswers.every(itemAnswer => {
      var validationMessage = compareValidationResult(itemAnswer.detailData, this.detailsInfos);
      if (!validationMessage.isValid) {
        validationFail = true;
        this.alert.error(validationMessage.errorMessage);
        return false
      }
    });

    const request: IQuestionnaireSaveRequestHttp = {
      itemAnswers: itemAnswers,
      encodedResponse: this.recaptcha
    };

    return request;
  }

  private onResize() {
    setTimeout(() => {
      this.resizeContainerHeight();
    }, 100);
  }

  private resizeContainerHeight() {
    const message: TPostMessage = {
      name: EEvent.EvideFormHeightChange,
      data: {
        height: this.container?.nativeElement.scrollHeight
      }
    };
    this.parentWindow.postMessage(message);
  }

}
